// components/calculator/Calculator.js
'use client'

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { FaCalculator, FaTruck, FaCheck, FaArrowRight, FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { materials } from '@data/materials'
import { motion, AnimatePresence } from 'framer-motion'
import PhoneInput from '@components/ui/PhoneInput'
import Image from 'next/image'
import type { CalculatorFormData, OrderFormData } from '@/src/types/calculator'
import { calculatorFormSchema, orderFormSchema } from '@/src/types/calculator'

// Объединяем все материалы в один массив для удобства
const allMaterials = [
    ...materials.black,
    ...materials.color,
    ...materials.special,
    ...materials.recyclable
]

export default function Calculator() {
    const [isOpen, setIsOpen] = useState(false)
    const [showResult, setShowResult] = useState(false)
    const [showForm, setShowForm] = useState(false)

    // Форма калькулятора
    const {
        register: registerCalc,
        handleSubmit: handleSubmitCalc,
        watch: watchCalc,
        formState: { errors: errorsCalc }
    } = useForm<CalculatorFormData>({
        resolver: zodResolver(calculatorFormSchema),
        defaultValues: {
            metalId: allMaterials[0].id,
            weight: 1,
            needTransport: false,
            phone: ''
        }
    })

    // Форма заказа
    const {
        register: registerOrder,
        handleSubmit: handleSubmitOrder,
        setValue: setValueOrder,
        watch: watchOrder,
        formState: { errors: errorsOrder, isSubmitting },
        reset: resetOrder
    } = useForm<OrderFormData>({
        resolver: zodResolver(orderFormSchema)
    })

    // Наблюдаем за значениями калькулятора
    const metalId = watchCalc('metalId')
    const weight = watchCalc('weight')
    const needTransport = watchCalc('needTransport')

    // Получаем выбранный металл
    const selectedMetal = allMaterials.find(m => m.id === Number(metalId)) || allMaterials[0]

    // Рассчитываем стоимость
    const calculatePrice = () => {
        let totalPrice = parseFloat(selectedMetal.price) * weight
        if (weight < 100) {
            totalPrice *= 0.9
        }
        // Добавляем расчет стоимости транспорта
        if (needTransport) {
            totalPrice += 500 // Примерная стоимость транспорта
        }
        return totalPrice
    }

    const totalPrice = calculatePrice()

    // Обработчик отправки формы калькулятора
    const onCalculatorSubmit = (data: CalculatorFormData) => {
        setShowResult(true)
        // Логируем данные формы
        console.log('Calculator data:', data)
    }

    // Обработчик отправки формы заказа
    const onOrderSubmit = async (data: OrderFormData) => {
        try {
            // Используем данные формы
            const response = await fetch('/api/order', {
                method: 'POST',
                body: JSON.stringify(data)
            })
            if (!response.ok) throw new Error('Failed to submit order')
            
            toast.success('Ваша заявка успешно отправлена!')
            resetOrder()
            setShowForm(false)
            setShowResult(false)
        } catch (error) {
            // Логируем ошибку
            console.error('Order submission error:', error)
            toast.error('Произошла ошибка при отправке заявки')
        }
    }

    // Обработчик изменения телефона
    const handlePhoneChange = (value: string | undefined) => {
        if (value) {
            setValueOrder('phone', value);
        }
    };

    const toggleCalculator = () => {
        setIsOpen(!isOpen)
        if (!isOpen) {
            setShowResult(false)
            setShowForm(false)
        }
    }

    // При открытии формы заказа устанавливаем данные из калькулятора
    const handleOrderClick = () => {
        setValueOrder('metalName', selectedMetal.name)
        setValueOrder('weight', weight)
        setValueOrder('totalPrice', totalPrice)
        setShowForm(true)
    }

    return (
        <section className="relative py-8 md:py-12 overflow-hidden">
            {/* Фоновое изображение */}
            <div className="absolute inset-0 z-0">
                <Image
                    src="/banner/calculator-banner.webp"
                    alt="Металлолом фон"
                    fill
                    className="object-cover opacity-10"
                    quality={85}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white/80 to-white/90" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden">
                        {/* Заголовок и кнопка разворачивания */}
                        <button 
                            onClick={toggleCalculator}
                            className="w-full flex items-center justify-between p-5 md:p-6 focus:outline-none border-b border-gray-200 transition-colors hover:bg-blue-50/30"
                        >
                            <div className="flex items-center">
                                <div className="bg-[#1A3365] rounded-full p-2.5 mr-3">
                                    <FaCalculator size={20} className="text-white" />
                                </div>
                                <h2 className="text-xl md:text-2xl font-bold text-[#1A3365]">
                                    Калькулятор металобрухту
                                </h2>
                            </div>
                            <div className="bg-[#1A3365]/10 rounded-full p-2 transition-transform duration-300">
                                {isOpen ? <FaChevronUp size={20} className="text-[#1A3365]" /> : <FaChevronDown size={20} className="text-[#1A3365]" />}
                            </div>
                        </button>

                        {/* Содержимое калькулятора */}
                        <AnimatePresence>
                            {isOpen && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="p-5 md:p-6">
                                        <AnimatePresence mode="wait">
                                            {!showForm ? (
                                                <motion.div
                                                    key="calculator"
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -20 }}
                                                    transition={{ duration: 0.4 }}
                                                    className="min-h-[400px] md:min-h-[520px]"
                                                >
                                                    <form onSubmit={handleSubmitCalc(onCalculatorSubmit)} className={`${showResult ? 'mb-4' : ''} space-y-4`}>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                            {/* Выбор металла */}
                                                            <div className="col-span-1 md:col-span-2">
                                                                <label htmlFor="metalId" className="block text-sm font-medium mb-1.5 text-gray-700">
                                                                    Вид матеріалу
                                                                </label>
                                                                <select
                                                                    id="metalId"
                                                                    {...registerCalc('metalId')}
                                                                    className="w-full border-2 border-gray-200 rounded-lg p-2.5 focus:ring-2 focus:ring-[#1A3365] focus:border-[#1A3365] bg-white/70 backdrop-blur-sm shadow-sm text-base transition-all hover:border-[#1A3365]"
                                                                >
                                                                    {allMaterials.map((metal) => (
                                                                        <option key={metal.id} value={metal.id}>
                                                                            {metal.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {errorsCalc.metalId && (
                                                                    <p className="mt-1 text-sm text-red-500">{errorsCalc.metalId.message}</p>
                                                                )}
                                                            </div>

                                                            {/* Вес */}
                                                            <div>
                                                                <label htmlFor="weight" className="block text-sm font-medium mb-1.5 text-gray-700">
                                                                    Вага (кг)
                                                                </label>
                                                                <div className="relative">
                                                                    <input
                                                                        type="number"
                                                                        id="weight"
                                                                        {...registerCalc('weight')}
                                                                        min="1"
                                                                        step="1"
                                                                        className="w-full border-2 border-gray-200 rounded-lg p-2.5 focus:ring-2 focus:ring-[#1A3365] focus:border-[#1A3365] bg-white/70 backdrop-blur-sm shadow-sm pr-12 text-base transition-all hover:border-[#1A3365]"
                                                                    />
                                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                                                        кг
                                                                    </span>
                                                                </div>
                                                                {errorsCalc.weight && (
                                                                    <p className="mt-1 text-sm text-red-500">{errorsCalc.weight.message}</p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Нужен ли транспорт */}
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                id="needTransport"
                                                                {...registerCalc('needTransport')}
                                                                className="w-4 h-4 text-[#1A3365] border-gray-300 rounded focus:ring-[#1A3365]"
                                                            />
                                                            <label htmlFor="needTransport" className="text-sm text-gray-700">
                                                                Потрібен транспорт для вивозу
                                                            </label>
                                                        </div>

                                                        <div className="mt-4">
                                                            <button
                                                                type="submit"
                                                                className="w-full md:w-auto bg-[#FF6B00] hover:bg-[#ff7b00] text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center justify-center text-base"
                                                            >
                                                                {!showResult ? (
                                                                    <>
                                                                        Розрахувати вартість
                                                                        <FaArrowRight size={18} className="ml-2" />
                                                                    </>
                                                                ) : (
                                                                    'Перерахувати'
                                                                )}
                                                            </button>
                                                        </div>
                                                    </form>

                                                    <AnimatePresence>
                                                        {showResult && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: 20 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -20 }}
                                                                transition={{ duration: 0.4 }}
                                                                className="bg-gradient-to-br from-blue-50 to-blue-100/50 backdrop-blur-sm p-4 md:p-5 rounded-xl border border-blue-200 shadow-md mt-4"
                                                            >
                                                                <h3 className="text-lg md:text-xl font-bold mb-4 text-[#1A3365]">
                                                                    Результат розрахунку
                                                                </h3>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    <div className="space-y-2">
                                                                        <p className="text-gray-700 text-sm flex items-center">
                                                                            <span className="font-medium min-w-[100px]">Вид матеріалу:</span>
                                                                            <span className="ml-2">{selectedMetal.name}</span>
                                                                        </p>
                                                                        <p className="text-gray-700 text-sm flex items-center">
                                                                            <span className="font-medium min-w-[100px]">Вага:</span>
                                                                            <span className="ml-2">{weight} кг</span>
                                                                        </p>
                                                                        <p className="text-gray-700 text-sm flex items-center">
                                                                            <span className="font-medium min-w-[100px]">Ціна за кг:</span>
                                                                            <span className="ml-2">{selectedMetal.price} грн/кг</span>
                                                                        </p>
                                                                    </div>
                                                                    <div className="bg-white/50 backdrop-blur-sm p-4 rounded-lg border border-blue-100">
                                                                        <p className="text-sm text-gray-600 mb-1">Приблизна вартість:</p>
                                                                        <p className="text-2xl font-bold text-[#1A3365]">
                                                                            {totalPrice.toFixed(2)} грн
                                                                        </p>
                                                                        {weight < 100 && (
                                                                            <p className="text-xs text-gray-500 mt-1">
                                                                                * застосована знижка 10% для ваги менше 100 кг
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="mt-5 flex justify-center">
                                                                    <button
                                                                        onClick={handleOrderClick}
                                                                        className="bg-[#FF6B00] hover:bg-[#ff7b00] text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center justify-center text-base"
                                                                    >
                                                                        <FaTruck size={20} className="mr-2" />
                                                                        Замовити вивіз металобрухту
                                                                    </button>
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </motion.div>
                                            ) : (
                                                <motion.div
                                                    key="form"
                                                    initial={{ opacity: 0, x: 20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: -20 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 min-h-[400px] md:min-h-[520px]"
                                                >
                                                    <h3 className="text-base md:text-lg font-bold mb-4 text-[#1A3365]">Замовити вивіз металобрухту</h3>
                                                    <form onSubmit={handleSubmitOrder(onOrderSubmit)} className="space-y-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                                            <div>
                                                                <label htmlFor="name" className="block text-sm font-medium mb-1 text-gray-700">
                                                                    Ваше ім&apos;я *
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="name"
                                                                    {...registerOrder('name')}
                                                                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-[#1A3365] focus:border-[#1A3365] shadow-sm text-sm"
                                                                    placeholder="Введіть ваше ім'я"
                                                                />
                                                                {errorsOrder.name && (
                                                                    <p className="mt-1 text-sm text-red-500">{errorsOrder.name.message}</p>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <label htmlFor="phone" className="block text-sm font-medium mb-1 text-gray-700">
                                                                    Телефон *
                                                                </label>
                                                                <PhoneInput
                                                                    id="phone"
                                                                    name="phone"
                                                                    value={watchOrder('phone') || ''}
                                                                    onChange={handlePhoneChange}
                                                                    required={true}
                                                                    placeholder="+380"
                                                                    className="w-full bg-white/70 backdrop-blur-sm rounded-lg"
                                                                />
                                                                {errorsOrder.phone && (
                                                                    <p className="mt-1 text-sm text-red-500">{errorsOrder.phone.message}</p>
                                                                )}
                                                            </div>
                                                            <div className="md:col-span-2">
                                                                <label htmlFor="address" className="block text-sm font-medium mb-1 text-gray-700">
                                                                    Адреса для вивозу *
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="address"
                                                                    {...registerOrder('address')}
                                                                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-[#1A3365] focus:border-[#1A3365] shadow-sm text-sm"
                                                                    placeholder="Введіть адресу"
                                                                />
                                                                {errorsOrder.address && (
                                                                    <p className="mt-1 text-sm text-red-500">{errorsOrder.address.message}</p>
                                                                )}
                                                            </div>
                                                            <div className="md:col-span-2">
                                                                <label htmlFor="comments" className="block text-sm font-medium mb-1 text-gray-700">
                                                                    Коментар
                                                                </label>
                                                                <textarea
                                                                    id="comments"
                                                                    {...registerOrder('comments')}
                                                                    rows={2}
                                                                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-[#1A3365] focus:border-[#1A3365] shadow-sm text-sm"
                                                                    placeholder="Додаткова інформація"
                                                                ></textarea>
                                                            </div>
                                                        </div>

                                                        <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3 mt-4">
                                                            <button
                                                                type="submit"
                                                                disabled={isSubmitting}
                                                                className="w-full md:w-auto bg-[#FF6B00] hover:bg-opacity-90 text-white font-bold py-2.5 px-5 rounded-md transition-colors flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {isSubmitting ? (
                                                                    'Відправка...'
                                                                ) : (
                                                                    <>
                                                                        <FaCheck size={16} className="mr-1.5" />
                                                                        Відправити заявку
                                                                    </>
                                                                )}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowForm(false)}
                                                                className="w-full md:w-auto bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2.5 px-5 rounded-md transition-colors text-sm"
                                                            >
                                                                Повернутися до калькулятора
                                                            </button>
                                                        </div>
                                                    </form>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    )
}